import { CameraIntegration, epochTimeOfSelectedEvent } from './base';
import { router, i18n } from '@qsrsoft/myq-core';
import Bowser from 'bowser';
const baseURL = import.meta.env.VITE_APP_SECURITY_API_BASE_URL;

const messages = {
  en: {
    dtiq_location_access_denied: 'You do not have access to this dtiq location',
    dtiq_missing_client_config: 'Missing DTiQ client configuration',
    dtiq_invalid_host_environment: 'Invalid DTiQ host environment',
    dtiq_video_not_supported_os: 'DTiQ video player is not supported on an {osName} device.',
    dtiq_video_not_supported_browser: 'DTiQ video player is not supported using the {browserName} browser',
  },
  es: {
    dtiq_location_access_denied: 'No tiene acceso a esta ubicación de dtiq',
    dtiq_missing_client_config: 'Falta la configuración del cliente DTiQ',
    dtiq_invalid_host_environment: 'Entorno de host de DTiQ no válido',
    dtiq_video_not_supported_os: 'El reproductor de video de DTiQ no es compatible con un dispositivo {osName}.',
    dtiq_video_not_supported_browser: 'El reproductor de video de DTiQ no es compatible con el navegador {browserName}',
  },
  fr: {
    dtiq_location_access_denied: "Vous n'avez pas accès à cet établissement DTiQ",
    dtiq_missing_client_config: 'Configuration client DTiQ manquante',
    dtiq_invalid_host_environment: "Environnement d'hébergement DTiQ non valide",
    dtiq_video_not_supported_os: "Le lecteur vidéo DTiQ n'est pas pris en charge sur un appareil {osName}.",
    dtiq_video_not_supported_browser: "Le lecteur vidéo DTiQ n'est pas pris en charge avec le navigateur {browserName}",
  },
  'fr-ca': {
    dtiq_location_access_denied: "Vous n'avez pas accès à cet établissement DTiQ",
    dtiq_missing_client_config: 'Configuration client DTiQ manquante',
    dtiq_invalid_host_environment: "Environnement d'hébergement DTiQ non valide",
    dtiq_video_not_supported_os: "Le lecteur vidéo DTiQ n'est pas pris en charge sur un appareil {osName}.",
    dtiq_video_not_supported_browser: "Le lecteur vidéo DTiQ n'est pas pris en charge avec le navigateur {browserName}",
  },
};

Object.keys(messages).forEach((locale) => {
  i18n.global.mergeLocaleMessage(locale, messages[locale]);
});

export class DTIQ extends CameraIntegration {
  constructor(...args) {
    super(...args);
    this.cameraProviderName = 'DTIQ';

    this.isLoginThroughTheirCrappyPortal = true;
    this.getBrowserAndOS();
  }

  getBrowserAndOS() {
    const { browser, os } = Bowser.parse(window.navigator.userAgent);
    this.browserName = browser.name.toLowerCase();
    this.osName = os.name.toLowerCase();
  }

  checkForCredentials() {
    const accessToken = this.camera?.accessToken;
    if (!accessToken) {
      this.isAuthenticated = false;
      return;
    }

    if (accessToken === 'dtiq_location_access_denied') {
      this.isAuthenticated = false;
      throw Error(i18n.global.t('dtiq_location_access_denied'));
    }

    const invalidTokens = [
      'refresh_token_not_available',
      'refresh_token_invalid',
    ];

    if (invalidTokens.includes(accessToken)) {
      this.isAuthenticated = false;
      return;
    }

    this.isAuthenticated = true;
  }

  async submitCredentials() {
    let url;
    const {
      dtiqIdentityHost,
      dtiqHostEnvironment,
      dtiqClientId,
      dtiqRedirectUri,
      dtiqQaHost,
      stateQueryParamterValueForSecurity,
    } = this.camera;
    // debug logging removed (use a debug flag elsewhere if needed)
    if (!dtiqIdentityHost || !dtiqClientId || !dtiqRedirectUri)
      throw Error(i18n.global.t('dtiq_missing_client_config'));

    if (dtiqHostEnvironment === 'QA')
      url = `${dtiqQaHost}/DTiQ360IdentityServer/connect/authorize`;
    else if (dtiqHostEnvironment === 'PROD')
      url = `${dtiqIdentityHost}/connect/authorize`;
    else throw Error(i18n.global.t('dtiq_invalid_host_environment'));

    // set DTIQCamera in local storage
    localStorage.setItem('DTIQCamera', JSON.stringify(this.camera));
    // store current route (vue-router v4 uses a ref for currentRoute)
    try {
      const fullPath =
        router?.currentRoute?.value?.fullPath ||
        router?.currentRoute?.fullPath ||
        window.location.pathname;
      localStorage.setItem('DTIQRedirectPath', fullPath);
    } catch (_) {
      console.error('Error storing redirect path:', _);
      // fallback to window location
      localStorage.setItem('DTIQRedirectPath', window.location.pathname);
    }

    const params = {
      client_id: dtiqClientId,
      scope: 'openid SystemAPI locations cameras offline_access',
      response_type: 'code',
      response_mode: 'form_post',
      redirect_uri: `${dtiqRedirectUri}/security/dtiq_authorization_response`,
      // redirect_uri: 'https://pr-16835.staging-preview.myqsrsoft.com/security/dtiq_authorization_response',
      state: stateQueryParamterValueForSecurity,
    };

    url += `?${new URLSearchParams(params).toString()}`;

    // Set legitimate external navigation flag before navigating (if available)
    try {
      // Check if filterLoadTool utility is available (parent repo specific)
      if (
        typeof window !== 'undefined' &&
        window.setLegitimateExternalNavigation
      ) {
        window.setLegitimateExternalNavigation(true);
        console.log(
          '[DTIQ] Set legitimate external navigation flag before redirecting to:',
          url,
        );
      } else {
        console.debug(
          '[DTIQ] filterLoadTool not available, continuing without setting navigation flag',
        );
      }
    } catch (error) {
      console.debug('[DTIQ] Error setting navigation flag:', error);
    }

    window.location = url;
  }

  async fetchThumbnails() {
    // DTiQ retrieves thumbnails from the script
    return [];
  }

  getUrlVideo() {
    const { dtiqHostEnvironment, dtiqApiHost, dtiqQaHost } = this.camera;

    if (dtiqHostEnvironment === 'QA')
      return `${dtiqQaHost}/DTiQ360SystemAPI/GetVideoPanel`;
    if (dtiqHostEnvironment === 'PROD') return `${dtiqApiHost}/GetVideoPanel`;
    throw Error(i18n.global.t('dtiq_invalid_host_environment'));
  }

  async fetchVideo() {
    // DTiQ does not work on ios for either safari or chrome
    if (this.osName === 'ios') {
      throw Error(
        i18n.global.t('dtiq_video_not_supported_os', { osName: this.osName }),
      );
    }
    // DTiQ does not work on safari for both macos and ios
    if (this.browserName === 'safari') {
      throw Error(
        i18n.global.t('dtiq_video_not_supported_browser', { browserName: this.browserName }),
      );
    }

    const { time } = this.event;
    const epochMilliSeconds = epochTimeOfSelectedEvent({
      format: 'milli',
      time,
      vendor: 'dtiq',
    });

    const wholeUrl = `${baseURL}/security/dtiq-iframe/v1/${this.orgId}/${this.camera?.accessToken}/${this.camera.dtiqLocationId}/${epochMilliSeconds}`;

    const nowPlaying = {
      cameraSelectionOpen: true,
      cameraSelectionToggle: false,
      calendar: false,
      timeBar: true,
      forceInitTime: true,
      devInfoPanel: false,
      timeFrame: 30,
      playOnSetVideo: false,
    };

    nowPlaying.video = {
      src: `${wholeUrl}?${new URLSearchParams().toString()}`,
      type: 'iframesrc',
    };

    return nowPlaying;
  }
}
